import PropTypes from "prop-types";
import { useState } from "react";
import { format } from "date-fns";
import { Box, Checkbox, CircularProgress, IconButton, Switch, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { IconDotsVertical, IconGripVertical } from "@tabler/icons";
import { columntype } from "src/helper/publicFunction";
import { fontFamilyMono } from "src/theme/Typography";
import { iconButtonSx } from "src/components/shared/ActionButton";
import DropDown from "./DropDown";
import PersonAvatar from "src/components/shared/PersonAvatar";
import { formatPhone, humanize } from "src/components/shared/format";

// One row of the CMS list table. Cells follow `columns` (type + field name)
// in the order of `headCells`.

const EMPTY = "—";

// Values the API sometimes sends for "nothing".
const isBlank = (value) => value == null || value === "" || value === "undefined" || value === "null";

function formatDate(value, pattern) {
  if (isBlank(value)) return EMPTY;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? EMPTY : format(date, pattern);
}

const isOn = (value) => value == 1 || value === true || value === "true"; // eslint-disable-line eqeqeq

function rowName(row) {
  return row?.name || row?.title || row?.fullname || row?.username || row?.first_name || row?.email || (row?.id != null ? `#${row.id}` : "row");
}

const RowTable = ({
  row,
  index,
  handleClick,
  onPressItem,
  isSelected,
  Listdropdown,
  columns,
  withOption,
  headCells,
  onPressAnyAction,
  loadingEditdatatype,
  withDeleteItem,
  withEditItem,
  isLogs,
  provided,
  snapshot,
  rowLabel,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isItemSelected = isSelected(row.id);
  const label = rowLabel || rowName(row);
  const options = (Listdropdown || []).filter((option) => option && option.name);

  const toggle = (column) => {
    if (loadingEditdatatype) return;
    const value = row[column.name];
    let next;
    if (value == 1 || value == 0) next = value == 1 ? 0 : 1; // eslint-disable-line eqeqeq
    else if (value === true || value === false) next = !value;
    else if (value === "true" || value === "false") next = value === "true" ? "false" : "true";
    else next = 1;
    onPressAnyAction(row, column.name, next);
  };

  const cellFor = (head) => {
    const column = columns?.find((c) => c.name === head.id);
    if (!column) return null;
    const value = row[column.name];
    const open = () => onPressItem(row);
    const busy = loadingEditdatatype === `${row.id}-${column.name}`;

    switch (column.type) {
      case columntype.time:
        return <TableCell key={head.id} onClick={open} sx={{ whiteSpace: "nowrap" }}>{formatDate(value, "HH:mm")}</TableCell>;
      case columntype.date:
        return <TableCell key={head.id} onClick={open} sx={{ whiteSpace: "nowrap" }}>{formatDate(value, "d MMM yyyy")}</TableCell>;
      case columntype.dateAndtime:
        return <TableCell key={head.id} onClick={open} sx={{ whiteSpace: "nowrap" }}>{formatDate(value, "d MMM yyyy, HH:mm")}</TableCell>;
      case columntype.html:
        return (
          <TableCell key={head.id} onClick={open}>
            <Box sx={{ maxWidth: 420, "& p": { m: 0 } }} dangerouslySetInnerHTML={{ __html: value ?? "" }} />
          </TableCell>
        );
      case columntype.image:
        // A missing or failed photo shows initials on a neutral disc, never
        // a broken image.
        return (
          <TableCell key={head.id} onClick={open} sx={{ py: 1 }}>
            <PersonAvatar name={String(label)} src={isBlank(value) ? undefined : String(value)} size={40} />
          </TableCell>
        );
      case columntype.status:
        return (
          <TableCell key={head.id} onClick={open}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                aria-hidden
                sx={{ bgcolor: isBlank(value) ? "error.main" : "success.main", borderRadius: "100%", height: 8, width: 8, flexShrink: 0 }}
              />
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                {isBlank(value) ? EMPTY : value}
              </Typography>
            </Box>
          </TableCell>
        );
      case columntype.paymentStatus: {
        const paid = !(value == 0); // eslint-disable-line eqeqeq
        return (
          <TableCell key={head.id} onClick={open}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box aria-hidden sx={{ bgcolor: paid ? "success.main" : "error.main", borderRadius: "100%", height: 8, width: 8 }} />
              <Typography variant="body2">{paid ? "Yes" : "No"}</Typography>
            </Box>
          </TableCell>
        );
      }
      case columntype.switch:
        return (
          <TableCell key={head.id} sx={{ width: 72 }}>
            {busy ? (
              <CircularProgress size={22} aria-label="Saving" />
            ) : (
              <Switch
                disabled={!withEditItem}
                checked={isOn(value)}
                onChange={() => toggle(column)}
                inputProps={{ "aria-label": `${head.label || column.name}: ${label}` }}
              />
            )}
          </TableCell>
        );
      case columntype.checkbox:
        return (
          <TableCell key={head.id}>
            {busy ? (
              <CircularProgress size={22} aria-label="Saving" />
            ) : (
              <Checkbox
                checked={isOn(value)}
                onChange={() => toggle(column)}
                inputProps={{ "aria-label": `${head.label || column.name}: ${label}` }}
              />
            )}
          </TableCell>
        );
      default: {
        // column.format: "label" turns slugs into words ("super-admin" ->
        // "Super admin"), "phone" groups digits.
        const text = isBlank(value)
          ? EMPTY
          : column.format === "label"
          ? humanize(value)
          : column.format === "phone"
          ? formatPhone(value)
          : String(value);
        const short = column.isSmall && text.length > 30 ? `${text.slice(0, 30)}…` : text;
        return (
          <TableCell
            key={head.id}
            onClick={open}
            title={short !== text ? text : undefined}
            sx={{
              color: text === EMPTY ? "text.secondary" : undefined,
              overflowWrap: "break-word",
              ...(head.id === "id" && { fontFamily: fontFamilyMono, fontSize: "0.8125rem", whiteSpace: "nowrap" }),
            }}
          >
            {short}
          </TableCell>
        );
      }
    }
  };

  const showActions = withOption && (withEditItem || withDeleteItem) && (!isLogs || row.action_type === "delete") && options.length > 0;

  return (
    <TableRow
      hover
      aria-selected={withDeleteItem ? isItemSelected : undefined}
      selected={isItemSelected}
      {...provided?.draggableProps}
      ref={provided?.innerRef}
      sx={{
        ...(snapshot?.isDragging && { bgcolor: "background.paper", boxShadow: (t) => t.shadows[9], display: "table" }),
        transition: "background-color 0.2s ease",
      }}
    >
      {/* Reordering: the handle is its own labelled button, so the row is
          not a control that contains other controls. Space lifts the row,
          arrows move it, space drops it. */}
      {provided && (
        <TableCell padding="checkbox" sx={{ width: 48 }}>
          <Tooltip title="Drag to reorder">
            <IconButton
              {...provided.dragHandleProps}
              component="span"
              role="button"
              aria-label={`Reorder ${label}`}
              size="small"
              sx={[iconButtonSx, { cursor: "grab", color: "text.secondary" }]}
            >
              <IconGripVertical size={18} aria-hidden />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
      {withDeleteItem && (
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={isItemSelected}
            onChange={() => handleClick(row.id)}
            inputProps={{ "aria-label": `Select ${label}` }}
          />
        </TableCell>
      )}

      {headCells.filter((head) => head.id !== "action").map(cellFor)}

      <TableCell align="right" className="table-sticky-actions" sx={{ width: 56, pr: 1 }}>
        {showActions && (
          <>
            <Tooltip title="Actions">
              <IconButton
                aria-label={`Actions for ${label}`}
                aria-haspopup="true"
                aria-expanded={anchorEl ? "true" : undefined}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={iconButtonSx}
              >
                <IconDotsVertical size={18} />
              </IconButton>
            </Tooltip>
            <DropDown Listdropdown={options} open={!!anchorEl} setOpen={() => setAnchorEl(null)} row={row} anchorEl={anchorEl} />
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

RowTable.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number,
  handleClick: PropTypes.func,
  onPressItem: PropTypes.func,
  isSelected: PropTypes.func,
  Listdropdown: PropTypes.array,
  columns: PropTypes.array,
  withOption: PropTypes.bool,
  headCells: PropTypes.array,
  onPressAnyAction: PropTypes.func,
  loadingEditdatatype: PropTypes.string,
  withDeleteItem: PropTypes.bool,
  withEditItem: PropTypes.bool,
  isLogs: PropTypes.bool,
  provided: PropTypes.object,
  snapshot: PropTypes.object,
  rowLabel: PropTypes.string,
};

export default RowTable;
